'use client';

import { useId, useMemo, useState } from 'react';

import { cn, getBackgroundClass } from '@workspace/lib';
import { Button, Icon, Picture, Tabs, Typography } from '@workspace/ui/common';
import { imageGalleryVariants } from '@workspace/blocks/ImageGallery/imageGallery.variants';

import type { ImageGalleryProps } from './image-gallery.types';
import { ImageGalleryModal } from './ImageGalleryModal';

const MAX_IMAGES = 6;
const MAX_GROUPS = 10;

export function ImageGallery({ block }: ImageGalleryProps) {
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);
  const tabsId = useId();

  const groups = useMemo(() => (block?.imageGroups ?? []).slice(0, MAX_GROUPS), [block?.imageGroups]);
  const showTabs = groups.length > 1;

  const selectedIndex = Math.min(activeGroupIndex, Math.max(0, groups.length - 1));
  const selectedGroup = groups[selectedIndex];

  const images = useMemo(
    () => (selectedGroup?.images ?? []).filter((item) => Boolean(item?.bynderImage)).slice(0, MAX_IMAGES),
    [selectedGroup?.images],
  );

  const tabs = useMemo(
    () =>
      groups.map((group, index) => ({
        key: group._key ?? index,
        label: group.groupTitle?.trim() || `Group ${index + 1}`,
      })),
    [groups],
  );

  if (!block || !groups.length) return null;

  const backgroundClass = getBackgroundClass(block.backgroundColor ?? 'bg-white');
  const linkUrl = block.link?.href ?? block.link?.url ?? (block.link?.anchor ? `#${block.link.anchor}` : undefined);

  const { container, header, heading, link, grid, imageWrapper } = imageGalleryVariants({ hasTabs: showTabs });

  return (
    <section className={cn('w-full', backgroundClass)}>
      <div className={cn('porta-container', container())}>
        <header className={cn('flex items-start justify-between', header())}>
          {block.heading?.trim() && (
            <Typography variant="h1" className={heading()}>
              {block.heading}
            </Typography>
          )}

          {linkUrl && (
            <Button
              variant="text"
              overrideClasses
              url={linkUrl}
              openInNewTab={Boolean(block.link?.isOpenInNewTab)}
              className={cn(
                'group inline-flex shrink-0 cursor-pointer items-center justify-center hover:underline focus-visible:outline-2 focus-visible:outline-offset-2',
                link(),
              )}
            >
              {block.linkText?.trim() || 'View All'}
              <Icon
                name="arrowRightIcon"
                className="transition-transform duration-300 ease-in-out group-hover:translate-x-1"
              />
            </Button>
          )}
        </header>
        {showTabs && (
          <Tabs
            items={tabs}
            activeIndex={selectedIndex}
            onChange={setActiveGroupIndex}
            idPrefix={tabsId}
            panelId={`${tabsId}-panel`}
            ariaLabel={`${block.heading?.trim() || 'Image gallery'} categories`}
          />
        )}
        <div
          id={`${tabsId}-panel`}
          role={showTabs ? 'tabpanel' : undefined}
          aria-labelledby={showTabs ? `${tabsId}-tab-${selectedIndex}` : undefined}
          className={cn('grid grid-cols-2 md:grid-cols-3', grid())}
        >
          {images.map((item, index) => {
            const image = item.bynderImage;
            const alt = item.alt ?? item.bynderImage?.alt ?? '';

            return (
              <Button
                key={item._key ?? index}
                variant="text"
                overrideClasses
                aria-label={alt ? `Open ${alt}` : `Open gallery image ${index + 1} of ${images.length}`}
                onClick={() => setActiveImageIndex(index)}
                className={cn(
                  'group cursor-pointer overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 md:col-span-1',
                  imageWrapper(),
                )}
              >
                <Picture
                  image={image ?? undefined}
                  alt={alt}
                  desktopWidth={480}
                  desktopHeight={518}
                  mobileWidth={179}
                  mobileHeight={212}
                  className="block size-full"
                  imgClassName="size-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.03] motion-reduce:transform-none"
                />
              </Button>
            );
          })}
        </div>
      </div>

      <ImageGalleryModal
        images={images}
        activeIndex={activeImageIndex ?? 0}
        isOpen={activeImageIndex !== null}
        onChange={setActiveImageIndex}
        onClose={() => setActiveImageIndex(null)}
      />
    </section>
  );
}
